import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ModalController } from '@ionic/angular';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { firstValueFrom, timeout } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { ForcedUpdateComponent } from '../../../presentations/components/design-system/organisms/forced-update/forced-update.component';
import { isOlderThan, readRequiredVersion, StorePlatform } from './minimum-version';

/**
 * Shows the update screen when this copy of the application is older than the
 * API accepts, and takes it away when it no longer is.
 *
 * WHY IT NEVER THROWS AND NEVER WAITS LONG. This runs while the application
 * starts. An API that is slow, down,
 * or older than this route, answers nothing, and nothing means "open".
 *
 * It asks again every time the application comes back to the front, because
 * that is the moment a member returns from the store, and the moment a minimum
 * raised during the day reaches people who never close the application.
 */
@Injectable({ providedIn: 'root' })
export class MinimumVersionService {
  private readonly http = inject(HttpClient);
  private readonly modalCtrl = inject(ModalController);

  /** Three seconds: long enough for a slow network, short enough not to be felt. */
  private static readonly PATIENCE = 3000;

  private screen?: HTMLIonModalElement;
  /** Only this service may close the screen; the back button and gestures may not. */
  private closing = false;

  /** Answers whether the application is now blocked. */
  async check(): Promise<boolean> {
    try {
      const platform = Capacitor.getPlatform();
      if (!Capacitor.isNativePlatform() || (platform !== 'ios' && platform !== 'android')) {
        return false;
      }

      const [{ version }, required] = await Promise.all([App.getInfo(), this.ask(platform)]);
      const blocked = required !== null && isOlderThan(version, required);

      await (blocked ? this.show(platform, version, required) : this.hide());
      return blocked;
    } catch (error) {
      console.warn('[MinimumVersion] the check failed, the application opens', error);
      return false;
    }
  }

  private async ask(platform: StorePlatform): Promise<string | null> {
    try {
      const answer = await firstValueFrom(
        this.http
          .get<unknown>(`${environment.api}/api/v1/app/minimum-version`)
          .pipe(timeout(MinimumVersionService.PATIENCE)),
      );
      return readRequiredVersion(answer, platform);
    } catch {
      return null;
    }
  }

  private async show(platform: StorePlatform, installed: string, required: string): Promise<void> {
    if (this.screen) {
      return;
    }
    this.screen = await this.modalCtrl.create({
      component: ForcedUpdateComponent,
      componentProps: { platform, installed, required },
      backdropDismiss: false,
      canDismiss: async () => this.closing,
    });
    await this.screen.present();
  }

  private async hide(): Promise<void> {
    if (!this.screen) {
      return;
    }
    this.closing = true;
    try {
      await this.screen.dismiss();
    } finally {
      this.closing = false;
      this.screen = undefined;
    }
  }
}
